import { Icon } from '../../Icon'
import type { IconName } from '../../Icon'
import type { QuickRefActivity } from '../../../types'

// Activities tab — prototype L3702-3730. 2-column responsive grid of
// activity cards, one per `dbo.Activities` row for the event (only
// is_enabled = 1, ordered by sort_order then name).
//
// Discovery surfaced (13 May 2026): 0 of 15 rows in dbo.Activities have a
// non-NULL icon today, and only ~0.3 activities per event on average — so
// most cards will fall back to the `activity` outline and most events
// will hit the empty-state card. The empty-state copy is written to read
// well on its own; it'll be seen more often than the grid until activity
// authoring picks up.

// Whitelist of valid Icon slugs — anything not in this set falls back to
// 'activity' so a stray DB value can't blow up the SVG render.
const KNOWN_ICONS: ReadonlyArray<IconName> = [
  'activity', 'alert-triangle', 'arrow-left', 'bar-chart', 'bell', 'book-open',
  'box', 'calendar', 'check-circle', 'chevronDown', 'chevronLeft', 'chevronRight',
  'clock', 'coffee', 'download', 'edit', 'eye', 'file', 'filter', 'first-aid',
  'flag', 'grid', 'grip', 'home', 'info', 'list', 'list-tree', 'lock', 'map',
  'message-square', 'phone', 'plus', 'search', 'settings', 'shield-check',
  'siren', 'star', 'target', 'train', 'users', 'x'
]

function iconFor(name: string | null | undefined): IconName {
  if (name && (KNOWN_ICONS as readonly string[]).includes(name)) {
    return name as IconName
  }
  return 'activity'
}

interface Props {
  activities: QuickRefActivity[]
}

export function ActivitiesTab({ activities }: Props) {
  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-2">Today’s Activities</h3>
      <p className="text-sm text-ink-soft mb-4">
        Everything running for visitors today. Helpful for answering “what else is on?” —
        pull up this list while you’re chatting to a family.
      </p>

      {activities.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {activities.map((a) => (
            <ActivityCard key={a.id} activity={a} />
          ))}
        </div>
      )}
    </div>
  )
}

function ActivityCard({ activity }: { activity: QuickRefActivity }) {
  return (
    <div className="bg-white border border-line rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <span
          aria-hidden
          className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-brass-soft text-brass flex-shrink-0"
        >
          <Icon name={iconFor(activity.icon)} size={14} />
        </span>
        <div className="font-semibold text-sm truncate">{activity.name}</div>
      </div>
      {activity.description && (
        <div className="text-xs text-ink-soft mb-2 leading-snug line-clamp-2">
          {activity.description}
        </div>
      )}
      {activity.location && (
        <div className="text-xs text-ink-soft flex items-center gap-1.5 mb-0.5">
          <Icon name="map" size={11} />
          <span className="truncate">{activity.location}</span>
        </div>
      )}
      {activity.timeText && (
        <div className="text-xs text-ink-soft flex items-center gap-1.5">
          <Icon name="clock" size={11} />
          <span className="truncate">{activity.timeText}</span>
        </div>
      )}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="bg-parchment border border-line rounded-lg p-5 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brass-soft text-brass mb-2">
        <Icon name="star" size={20} />
      </div>
      <div className="font-semibold text-sm mb-1">No activities recorded yet</div>
      <div className="text-xs text-ink-soft max-w-sm mx-auto leading-snug">
        Once a programme is set, every visitor-facing thing on the day shows up here —
        names, locations, times. Add them on the Activities tab of the event page.
      </div>
    </div>
  )
}
